import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePagination, useSortBy, useTable } from "react-table";
import { IoIosAddCircleOutline } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import { VscEye } from "react-icons/vsc";
import { toast } from "react-toastify";
import { getLabTestLookups } from "../../../../Service/labTests";
import Loader from "../../../../reusableUI/loader";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";
import CommonDropdown from "../../../../reusableUI/dropdown";
import { CommonImages } from "../../../../reusableUI/images";
import { flattenLabTestLookups } from "./labTestShared";

const PAGE_COUNT_OPTIONS = [25, 50, 75, 100].map((value) => ({
  label: String(value),
  value,
}));

const LabTests = ({ searchTerm = "" }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tests, setTests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationPage, setPaginationPage] = useState(0);
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const savedPageCount = localStorage.getItem("labTestsPageCount");
    return savedPageCount ? Number(savedPageCount) : 50;
  });

  const loadTests = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await getLabTestLookups();
      if (response?.error !== false) {
        throw new Error(response?.message || "Unable to load lab tests.");
      }
      setTests(flattenLabTestLookups(response));
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || "Unable to load lab tests.";
      setLoadError(message);
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  const filteredData = useMemo(() => {
    const terms = [searchTerm, searchQuery]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);

    if (!terms.length) return tests;

    return tests.filter((test) => {
      const searchable = [test.parameterName, test.department, test.testGroup]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      return terms.every((term) => searchable.includes(term));
    });
  }, [searchQuery, searchTerm, tests]);

  const columns = useMemo(
    () => [
      {
        Header: "Parameter Name",
        accessor: "parameterName",
        id: "parameterName",
        width: 220,
        minWidth: 170,
        maxWidth: 280,
        Cell: ({ value }) => (
          <span className="inline-block max-w-[220px] truncate text-[12px] text-blue-600" title={value || "-"}>
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Department",
        accessor: "department",
        id: "department",
        width: 150,
        minWidth: 120,
        maxWidth: 190,
        Cell: ({ value }) => <span className="text-[12px]">{value || "-"}</span>,
      },
      {
        Header: "Test Group",
        accessor: "testGroup",
        id: "testGroup",
        width: 180,
        minWidth: 140,
        maxWidth: 220,
        Cell: ({ value }) => <span className="text-[12px]">{value || "-"}</span>,
      },
      {
        Header: "Sample Type",
        accessor: "sampleType",
        id: "sampleType",
        width: 190,
        minWidth: 150,
        maxWidth: 240,
        Cell: ({ value }) => <span className="text-[12px]">{value || "-"}</span>,
      },
      {
        Header: "Unit",
        accessor: "unit",
        id: "unit",
        width: 120,
        minWidth: 90,
        maxWidth: 150,
        align: "center",
        Cell: ({ value }) => <span className="text-[12px]">{value || "-"}</span>,
      },
      {
        Header: "Action",
        accessor: "action",
        id: "action",
        width: 100,
        minWidth: 90,
        maxWidth: 120,
        disableSortBy: true,
        align: "center",
        Cell: ({ row }) => (
          <button
            type="button"
            className="inline-flex items-center justify-center text-blue-600 transition-opacity hover:opacity-70"
            aria-label={`View ${row.original?.parameterName || "lab test"}`}
            title="View lab test"
            onClick={() => {
              if (!row.original?._id) return;
              navigate(`/admin/lab-tests/${row.original._id}`, {
                state: { labTest: row.original },
              });
            }}
          >
            <VscEye size={18} />
          </button>
        ),
      },
    ],
    [navigate],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    setPageSize,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
      },
      autoResetPage: false,
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (pageIndex !== paginationPage) setPaginationPage(pageIndex);
  }, [pageIndex, paginationPage]);

  useEffect(() => {
    setPageSize(selectedPageCount);
    gotoPage(0);
    setPaginationPage(0);
  }, [gotoPage, selectedPageCount, setPageSize]);

  useEffect(() => {
    gotoPage(0);
    setPaginationPage(0);
  }, [gotoPage, searchQuery, searchTerm]);

  const handleChangePageCount = (event) => {
    const newValue = Number(event.target.value);
    setSelectedPageCount(newValue);
    localStorage.setItem("labTestsPageCount", String(newValue));
  };

  const addLabTest = () => navigate("/admin/lab-tests/new");

  if (loading) {
    return (
      <div className="flex h-[85vh] items-center justify-center bg-[#F3F6F5]">
        <Loader loading={loading} />
      </div>
    );
  }

  return (
    <div className="bg-[#F3F6F5]">
      <div className="ml-2 flex h-[89vh] w-auto flex-col gap-3 overflow-x-auto rounded-2xl bg-white shadow-lg">
        <div className="mt-[-20px] flex items-center justify-between">
          <div className="mt-8 flex items-center p-2">
            <p className="ml-2 text-[14px] font-bold">Lab Test List ({filteredData.length})</p>
          </div>

          <div className="mr-4 mt-6 flex items-center gap-3 p-1">
            <div className="relative w-[250px] max-w-[35vw]">
              <IoSearchOutline className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search lab tests"
                aria-label="Search lab tests"
                className="w-full rounded-md border border-gray-300 py-1 pl-8 pr-2 text-[12px] outline-none focus:border-blue-500"
              />
            </div>
            <div className="w-[80px]" title="Set the page lab-test count">
              <CommonDropdown
                options={PAGE_COUNT_OPTIONS}
                value={selectedPageCount}
                onChange={handleChangePageCount}
                page="labTests"
              />
            </div>
            <button
              type="button"
              className="flex flex-row items-center gap-1 rounded-md bg-custom-gradient p-2"
              onClick={addLabTest}
            >
              <IoIosAddCircleOutline color="white" size={18} />
              <span className="whitespace-nowrap text-[12px] text-white">Add Lab Test</span>
            </button>
            <button
              type="button"
              className="text-[12px] text-blue-600 hover:underline"
              onClick={() => {
                loadTests();
                toast.info("Refreshing lab tests");
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-[-15px] flex-1 overflow-x-auto">
          <div className="min-w-[900px]">
            <ReusableTable
              columns={columns}
              getTableProps={getTableProps}
              getTableBodyProps={getTableBodyProps}
              headerGroups={headerGroups}
              tablePage={page}
              prepareRow={prepareRow}
              filterIcon={CommonImages.filter}
              searchTerm={searchTerm}
              errorMsg={loadError || (filteredData.length === 0 ? "No lab tests found." : undefined)}
            />
          </div>
        </div>

        {filteredData.length > 0 && (
          <div className="mb-2">
            <Page
              paginationPage={paginationPage}
              nextPage={nextPage}
              previousPage={previousPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              screen="labTests"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LabTests;
